/**
 * Offset inference and layout validation.
 *
 * Arduino's "Export Compiled Binary" drops three or four .bin files in a folder
 * with no indication of where any of them belong. Getting those offsets wrong
 * is the single most common reason a hand-rolled web flasher "works" but leaves
 * a board boot-looping. We infer the role of each file from its name, confirm
 * that guess against the bytes, and then check the resulting layout for the
 * mistakes that actually brick people: overlaps, wrong-chip images, and
 * flashing an app over an erased chip with no bootloader.
 */

import { CHIPS, formatOffset, type ChipProfile } from "./chips";
import {
  formatBytes,
  looksLikeMergedImage,
  parseImageHeader,
  parsePartitionTable,
  type EspImageHeader,
  type PartitionEntry,
} from "./binaryInfo";

export type PartRole =
  | "bootloader"
  | "partition-table"
  | "ota-data"
  | "app"
  | "filesystem"
  | "merged"
  | "unknown";

export const ROLE_LABELS: Record<PartRole, string> = {
  bootloader: "Bootloader",
  "partition-table": "Partition table",
  "ota-data": "OTA data",
  app: "Application",
  filesystem: "Filesystem",
  merged: "Merged image",
  unknown: "Unknown",
};

export interface BinaryAnalysis {
  role: PartRole;
  /** How the role was determined -- surfaced so users can tell a guess from a fact. */
  basis: "content" | "filename" | "filename+content" | "guess";
  header: EspImageHeader | null;
  partitions: PartitionEntry[] | null;
  /** Chip the image declares it was built for, when the header carries one. */
  declaredChip: ChipProfile | undefined;
  suggestedOffset: number | null;
  /** Roles whose address is fixed by the boot ROM; the UI locks these. */
  offsetIsFixed: boolean;
  notes: string[];
}

export interface FirmwarePart {
  id: string;
  fileName: string;
  data: Uint8Array;
  offset: number;
  offsetSource: "auto" | "manual";
  include: boolean;
  analysis: BinaryAnalysis;
}

interface FilenameRule {
  role: PartRole;
  test: RegExp;
}

// Evaluated in order; the first match wins. Patterns cover Arduino IDE,
// PlatformIO, and ESP-IDF output naming.
const FILENAME_RULES: FilenameRule[] = [
  { role: "merged", test: /(^|[._-])merged([._-]|\.bin$)/i },
  { role: "bootloader", test: /bootloader/i },
  { role: "partition-table", test: /partition[-_.]?table|partitions/i },
  { role: "ota-data", test: /boot[-_]?app0|ota[-_]?data/i },
  { role: "filesystem", test: /spiffs|littlefs|fatfs|filesystem|\bfs\b/i },
  { role: "app", test: /\.bin$/i },
];

function roleFromFilename(fileName: string): PartRole | null {
  const base = fileName.split(/[\\/]/).pop() ?? fileName;
  return FILENAME_RULES.find((rule) => rule.test.test(base))?.role ?? null;
}

/**
 * Inspect a file and work out what it is and where it goes.
 * `chip` is the connected (or user-selected) target, used to resolve
 * chip-dependent addresses such as the bootloader offset.
 */
export function analyzeBinary(
  fileName: string,
  data: Uint8Array,
  chip: ChipProfile | undefined,
): BinaryAnalysis {
  const notes: string[] = [];
  const nameRole = roleFromFilename(fileName);

  const header = parseImageHeader(data, 0);
  const partitions = parsePartitionTable(data, 0);
  const merged = looksLikeMergedImage(data);

  // Content beats filename: a file named firmware.bin that is actually a merged
  // whole-flash image must go to 0x0, and writing it to 0x10000 destroys the
  // partition table.
  let role: PartRole;
  let basis: BinaryAnalysis["basis"];

  if (merged) {
    role = "merged";
    basis = nameRole === "merged" ? "filename+content" : "content";
    if (nameRole !== "merged") {
      notes.push(
        "Detected as a merged whole-flash image from its contents (it contains a partition table at 0x8000), even though the filename does not say so.",
      );
    }
  } else if (partitions) {
    role = "partition-table";
    basis = nameRole === "partition-table" ? "filename+content" : "content";
  } else if (nameRole) {
    role = nameRole;
    basis = header ? "filename+content" : "filename";
  } else if (header) {
    role = "app";
    basis = "content";
  } else {
    role = "unknown";
    basis = "guess";
  }

  // Anything we could not place, and anything claiming to be code without the
  // 0xE9 magic, gets an explicit note. Silently showing "Unknown" with no
  // address is the behaviour that leaves people stuck.
  if (role === "unknown") {
    notes.push(
      "This file was not recognised as any kind of ESP image: it has neither a flashable name nor the 0xE9 magic byte that starts every ESP application image. The .elf, .map, and .json files sitting next to your .bin exports are build artefacts and cannot be flashed. If this really is firmware, set its offset manually.",
    );
  } else if ((role === "app" || role === "bootloader") && !header) {
    notes.push(
      "This file does not start with the 0xE9 magic byte that every ESP application image begins with. It may be an .elf, a source file, or a truncated download rather than a flashable binary.",
    );
  }

  const target = chip ?? header?.chip;
  const suggestedOffset = resolveOffset(role, target, partitions);
  const offsetIsFixed = role === "partition-table" || role === "merged";

  if (role === "bootloader" && header?.chip && chip && header.chip.key !== chip.key) {
    notes.push(
      `The bootloader header says it was built for ${header.chip.name}, but the layout is being computed for ${chip.name}.`,
    );
  }

  if (role === "filesystem" && suggestedOffset === null) {
    notes.push(
      "Filesystem images have no fixed address -- it comes from your partition table. Add the partition table file and the offset will be filled in, or type it manually.",
    );
  }

  return {
    role,
    basis,
    header,
    partitions,
    declaredChip: header?.chip,
    suggestedOffset,
    offsetIsFixed,
    notes,
  };
}

function resolveOffset(
  role: PartRole,
  chip: ChipProfile | undefined,
  partitions: PartitionEntry[] | null,
): number | null {
  const profile = chip ?? CHIPS.esp32;

  switch (role) {
    case "merged":
      return 0x0;
    case "bootloader":
      return profile.bootloaderOffset;
    case "partition-table":
      return profile.partitionTableOffset ?? 0x8000;
    case "ota-data":
      return profile.otaDataOffset ?? 0xe000;
    case "app":
      return profile.appOffset;
    case "filesystem":
      // Only resolvable once we can see the partition table.
      return partitions?.find((p) => ["spiffs", "littlefs", "fat"].includes(p.subTypeName))?.offset ?? null;
    default:
      return null;
  }
}

/**
 * Re-derive automatic offsets across the whole set. Called when the target chip
 * changes or when a partition table is added, since both can move addresses
 * that were previously unknown. Manually-typed offsets are preserved.
 */
export function recomputeOffsets(parts: FirmwarePart[], chip: ChipProfile | undefined): FirmwarePart[] {
  const table = parts.find((p) => p.analysis.partitions)?.analysis.partitions ?? null;

  return parts.map((part) => {
    const analysis = { ...part.analysis };
    analysis.suggestedOffset = resolveOffset(analysis.role, chip ?? analysis.declaredChip, table);

    if (part.offsetSource === "manual" || analysis.suggestedOffset === null) {
      return { ...part, analysis };
    }
    return { ...part, analysis, offset: analysis.suggestedOffset };
  });
}

// ---------------------------------------------------------------------------
// Layout validation
// ---------------------------------------------------------------------------

export type IssueLevel = "error" | "warning" | "info";

export interface LayoutIssue {
  level: IssueLevel;
  title: string;
  detail: string;
  /** Ids of the parts involved, so the UI can highlight the offending rows. */
  partIds: string[];
}

export interface LayoutReport {
  issues: LayoutIssue[];
  /** Blocking issues exist -- the flash button should be disabled. */
  hasErrors: boolean;
  totalBytes: number;
  /** Highest address written, used against the detected flash size. */
  highestAddress: number;
}

export interface ValidateOptions {
  parts: FirmwarePart[];
  chip: ChipProfile | undefined;
  eraseAll: boolean;
  /** Detected flash capacity in bytes, when the chip has been probed. */
  flashSizeBytes: number | null;
}

export function validateLayout({
  parts,
  chip,
  eraseAll,
  flashSizeBytes,
}: ValidateOptions): LayoutReport {
  const active = parts.filter((p) => p.include);
  const issues: LayoutIssue[] = [];

  if (active.length === 0) {
    return { issues, hasErrors: false, totalBytes: 0, highestAddress: 0 };
  }

  // --- Overlapping regions -------------------------------------------------
  const regions = active
    .map((part) => ({ part, start: part.offset, end: part.offset + part.data.length }))
    .sort((a, b) => a.start - b.start);

  for (let i = 1; i < regions.length; i++) {
    const previous = regions[i - 1];
    const current = regions[i];
    if (current.start < previous.end) {
      issues.push({
        level: "error",
        title: "Two files overlap in flash",
        detail:
          `${previous.part.fileName} occupies ${formatOffset(previous.start)}-${formatOffset(previous.end)} ` +
          `but ${current.part.fileName} starts at ${formatOffset(current.start)}. ` +
          `The second write would corrupt the first. Adjust an offset or remove one of the files.`,
        partIds: [previous.part.id, current.part.id],
      });
    }
  }

  // --- Wrong chip ----------------------------------------------------------
  if (chip) {
    for (const part of active) {
      const declared = part.analysis.declaredChip;
      if (declared && declared.key !== chip.key) {
        issues.push({
          level: "error",
          title: `${part.fileName} was built for a different chip`,
          detail:
            `The image header declares ${declared.name}, but the connected board is ${chip.name}. ` +
            `Flashing it will produce a board that never boots. Rebuild in the Arduino IDE with ${chip.name} ` +
            `selected under Tools > Board.`,
          partIds: [part.id],
        });
      }
    }
  }

  // --- Duplicate roles -----------------------------------------------------
  const byRole = new Map<PartRole, FirmwarePart[]>();
  for (const part of active) {
    const list = byRole.get(part.analysis.role) ?? [];
    list.push(part);
    byRole.set(part.analysis.role, list);
  }
  for (const [role, list] of byRole) {
    if (list.length > 1 && role !== "unknown" && role !== "filesystem") {
      issues.push({
        level: "warning",
        title: `More than one ${ROLE_LABELS[role].toLowerCase()} selected`,
        detail:
          `${list.map((p) => p.fileName).join(", ")} were all identified as ${ROLE_LABELS[role].toLowerCase()} images. ` +
          `Usually only one belongs in a flash. Uncheck the ones you do not want written.`,
        partIds: list.map((p) => p.id),
      });
    }
  }

  // --- Merged image mixed with individual parts ----------------------------
  const mergedParts = byRole.get("merged") ?? [];
  if (mergedParts.length > 0 && active.length > mergedParts.length) {
    issues.push({
      level: "error",
      title: "A merged image cannot be combined with individual files",
      detail:
        `${mergedParts[0].fileName} already contains the bootloader, partition table, and application for the whole flash. ` +
        `Writing other files alongside it will overwrite parts of it. Flash the merged image on its own.`,
      partIds: active.map((p) => p.id),
    });
  }

  // --- Bootability ---------------------------------------------------------
  const hasBootloader = byRole.has("bootloader") || mergedParts.length > 0;
  const hasTable = byRole.has("partition-table") || mergedParts.length > 0;
  const hasApp = byRole.has("app") || mergedParts.length > 0;

  if (eraseAll && hasApp && (!hasBootloader || !hasTable)) {
    const missing = [!hasBootloader && "bootloader", !hasTable && "partition table"]
      .filter(Boolean)
      .join(" and ");
    issues.push({
      level: "error",
      title: `Erasing the whole flash without a ${missing}`,
      detail:
        `"Erase entire flash" wipes everything, so the ${missing} the board needs to start will be gone and only ` +
        `the application will be written back. The board will not boot. Either turn off "Erase entire flash", or ` +
        `add the missing file -- Arduino exports it next to your sketch binary.`,
      partIds: active.map((p) => p.id),
    });
  }

  if (!eraseAll && hasApp && !hasBootloader && chip?.key !== "esp8266") {
    issues.push({
      level: "info",
      title: "Application-only update",
      detail:
        "Only the application is being written, reusing the bootloader and partition table already on the board. " +
        "That is the right choice for updating a sketch. If the board has never been flashed, add the bootloader " +
        "and partition table too.",
      partIds: [],
    });
  }

  // --- Offset sanity against the chip --------------------------------------
  if (chip) {
    for (const part of active) {
      const expected = part.analysis.suggestedOffset;
      if (
        expected !== null &&
        part.offsetSource === "manual" &&
        part.offset !== expected &&
        part.analysis.role !== "unknown" &&
        part.analysis.role !== "filesystem"
      ) {
        issues.push({
          level: "warning",
          title: `${part.fileName} is at a non-standard offset`,
          detail:
            `A ${ROLE_LABELS[part.analysis.role].toLowerCase()} for ${chip.name} normally goes at ${formatOffset(expected)}, ` +
            `but this one is set to ${formatOffset(part.offset)}. That is fine if you use a custom partition layout -- ` +
            `otherwise reset it to the suggested address.`,
          partIds: [part.id],
        });
      }
    }
  }

  // --- Unplaceable files ---------------------------------------------------
  // A part with no inferable address falls back to offset 0, which on most
  // chips is where the bootloader lives. Writing an unidentified blob there
  // bricks the board, so require the user to place it deliberately.
  for (const part of active) {
    if (part.analysis.suggestedOffset === null && part.offsetSource === "auto") {
      issues.push({
        level: "error",
        title: `${part.fileName} has no known flash address`,
        detail:
          `This file could not be matched to a known role, so there is no address to write it to. It currently ` +
          `defaults to ${formatOffset(part.offset)}, which is where the bootloader lives on most chips -- writing ` +
          `the wrong data there stops the board booting. Type the correct offset, or uncheck the file if it was ` +
          `added by mistake.`,
        partIds: [part.id],
      });
    }
  }

  // --- Capacity ------------------------------------------------------------
  const highestAddress = Math.max(...regions.map((r) => r.end));
  const totalBytes = active.reduce((sum, part) => sum + part.data.length, 0);

  if (flashSizeBytes !== null && highestAddress > flashSizeBytes) {
    issues.push({
      level: "error",
      title: "Firmware does not fit in this board's flash",
      detail:
        `The layout writes up to ${formatOffset(highestAddress)} (${formatBytes(highestAddress)}), but the ` +
        `board reports only ${formatBytes(flashSizeBytes)} of flash. Choose a smaller partition scheme in the ` +
        `Arduino IDE under Tools > Partition Scheme, or use a board with more flash.`,
      partIds: [],
    });
  }

  // --- App fits its partition ---------------------------------------------
  const table = active.find((p) => p.analysis.partitions)?.analysis.partitions;
  const appParts = byRole.get("app") ?? [];
  if (table && appParts.length === 1) {
    const appPartition = table.find((p) => p.type === 0 && p.offset === appParts[0].offset);
    if (appPartition && appParts[0].data.length > appPartition.size) {
      issues.push({
        level: "error",
        title: "Sketch is larger than its partition",
        detail:
          `${appParts[0].fileName} is ${formatBytes(appParts[0].data.length)}, but the "${appPartition.label}" ` +
          `partition it targets is only ${formatBytes(appPartition.size)}. Pick a partition scheme with a larger ` +
          `app slot (Tools > Partition Scheme > "Huge APP" or "No OTA") and re-export.`,
        partIds: [appParts[0].id],
      });
    }
  }

  // --- Empty files ---------------------------------------------------------
  for (const part of active) {
    if (part.data.length === 0) {
      issues.push({
        level: "error",
        title: `${part.fileName} is empty`,
        detail: "The file contains no data. It may have failed to copy or the export may have been interrupted.",
        partIds: [part.id],
      });
    }
  }

  return {
    issues,
    hasErrors: issues.some((issue) => issue.level === "error"),
    totalBytes,
    highestAddress,
  };
}
